import toQuery from './lib/toQuery';

const formatQueryParams = (params?: Record<string, unknown>): string => {
    return params ? `?${toQuery(params)}` : '';
};

export const NavigationRoutes = {
    // Home
    home: () => '/',

    // Library
    library: () => '/library',

    // Threads
    threads: () => '/threads',

    // The signed-in user's profile (account summary, personal access tokens)
    profile: () => '/profile',
    personalAccessTokens: () => '/profile/tokens',

    // Jobs
    jobs: {
        root: () => '/jobs',
        detail: (id: string) => `/jobs/${id}`
    },

    // Analytics (every signed-in user; scope decides what they see)
    analytics: {
        root: () => '/analytics',
        activity: () => '/analytics/activity',
        tokens: () => '/analytics/tokens',
        roi: () => '/analytics/roi'
    },

    // Admin routes
    admin: {
        root: () => '/admin',
        // Configuration
        settings: () => '/admin/config/settings',
        branding: () => '/admin/config/branding',
        assistantDefaults: () => '/admin/config/defaults',
        models: () => '/admin/config/models',
        /** Opens the Models page with that catalog row's editor open. */
        modelDetail: (modelId: string) => `/admin/config/models/${modelId}`,
        aiProviders: () => '/admin/config/ai-providers',
        aiProviderDetail: (providerId: string | '_') =>
            `/admin/config/ai-providers/${providerId}`,
        migrateModels: () => '/admin/config/migrate-models',
        modelMigrationProgress: () => '/admin/config/migrate-models/progress',
        teams: () => '/admin/config/teams',
        applicationSecrets: () => '/admin/config/application-secrets',
        // Tools
        toolSettings: () => '/admin/tools/settings',
        webSearchSettings: () => '/admin/tools/web-search',
        microsoftGraphApiSettings: () => '/admin/tools/microsoft-graph-api',
        m365ToolSettings: () => '/admin/tools/m365-tools',
        plannerToolSettings: () => '/admin/tools/planner',
        helpfruitSettings: () => '/admin/tools/helpfruit',
        builtInToolsSettings: () => '/admin/tools/built-in',
        connectorSettings: () => '/admin/tools/connectors',
        mcpServers: () => '/admin/tools/mcp-servers',
        skills: () => '/admin/tools/skills',
        mcpActivityLogs: () => '/admin/tools/mcp-activity-logs',
        authConfigurations: () => '/admin/tools/auth-configurations',
        driveSyncSettings: () => '/admin/tools/drive-sync',
        // Management
        users: () => '/admin/mgt/users',
        userDetail: (userProfileId: string) =>
            `/admin/mgt/users/${userProfileId}`,
        profileChangeLog: () => '/admin/mgt/profile-changes',
        groups: () => '/admin/mgt/groups',
        groupDetail: (groupId: string) => `/admin/mgt/groups/${groupId}`,
        accessProfiles: () => '/admin/mgt/access-profiles',
        accessProfileDetail: (profileId: string) =>
            `/admin/mgt/access-profiles/${profileId}`,
        assistants: () => '/admin/mgt/assistants',
        approvals: () => '/admin/mgt/approvals',
        // Monitoring
        // Former homes of the analytics dashboards; kept only so old bookmarks redirect
        // (see LegacyAnalyticsRedirect). Link to NavigationRoutes.analytics.* instead.
        legacyAnalytics: () => '/admin/monitoring/analytics',
        legacyTokenAnalytics: () => '/admin/monitoring/token-analytics',
        legacyRoiAnalytics: () => '/admin/monitoring/roi-analytics',
        diagnostics: () => '/admin/monitoring/diagnostics',
        threadRunErrors: () => '/admin/monitoring/thread-run-errors',
        threadRunDebug: (
            assistantId: string,
            threadId: string,
            runId: string
        ) =>
            `/admin/monitoring/thread-run-errors/debug/${assistantId}/${threadId}/${runId}`,
        fileSyncOperations: () => '/admin/monitoring/file-sync-operations',
        driveSyncs: () => '/admin/monitoring/drive-syncs',
        jobs: () => '/admin/monitoring/jobs',
        jobDetail: (id: string) => `/admin/monitoring/jobs/${id}`,
        // System
        deploymentTasks: () => '/admin/sys/deployment-tasks',
        features: () => '/admin/sys/features',
        vectorStores: () => '/admin/sys/vector-stores',
        storeSettings: () => '/admin/sys/store-settings',
        // Entra ID Sync
        entraIdSyncConfig: () => '/admin/entra-id-sync/config',
        entraIdSyncHistory: () => '/admin/entra-id-sync/history',
        entraIdSyncGroupFilter: () => '/admin/entra-id-sync/group-filter',
        // Standalone
        billing: () => '/admin/billing'
    },

    // Assistant routes
    assistants: {
        detail: (id: string, params?: Record<string, unknown>) =>
            `/assistants/${id}${formatQueryParams(params)}`,
        details: {
            root: (id: string | '_') => `/assistants/${id}/details`,
            userGuide: (id: string) => `/assistants/${id}/details/user-guide`,
            share: (id: string) => `/assistants/${id}/details/share`,
            files: (id: string) => `/assistants/${id}/details/files`,
            certification: (id: string) =>
                `/assistants/${id}/details/certification`,
            template: (id: string) => `/assistants/${id}/details/template`,
            publishing: (id: string) => `/assistants/${id}/details/publishing`,
            integration: (id: string) =>
                `/assistants/${id}/details/integration`,
            mcp: (id: string) => `/assistants/${id}/details/mcp`,
            skills: (id: string) => `/assistants/${id}/details/skills`,
            auth: (id: string) => `/assistants/${id}/details/auth`,
            tools: (id: string) => `/assistants/${id}/details/tools`,
            forms: (id: string) => `/assistants/${id}/details/forms`,
            packageExport: (id: string) => `/assistants/${id}/details/package`,
            // Import creates an assistant, so it lives on the new-assistant screen ('_') beside Create.
            packageImport: () => `/assistants/_/details/import`,
            addToHome: (id: string) => `/assistants/${id}/details/add-to-home`,
            schedules: {
                root: (id: string) => `/assistants/${id}/details/schedules`,
                edit: (assistantId: string, scheduleId: string | '_') =>
                    `/assistants/${assistantId}/details/schedules/${scheduleId}`
            },
            driveSync: {
                root: (id: string) => `/assistants/${id}/details/drive-sync`,
                edit: (id: string, configId: string) =>
                    `/assistants/${id}/details/drive-sync/${configId}`
            },
            backgroundRuns: (id: string) =>
                `/assistants/${id}/details/background-runs`,
            batchPresets: {
                root: (id: string) => `/assistants/${id}/details/batch-presets`,
                edit: (id: string, presetId: string | '_') =>
                    `/assistants/${id}/details/batch-presets/${presetId}`
            },
            webCrawl: {
                root: (id: string) => `/assistants/${id}/details/webcrawl`,
                history: {
                    root: (id: string) =>
                        `/assistants/${id}/details/webcrawl/history`,
                    details: (id: string, runId: string) =>
                        `/assistants/${id}/details/webcrawl/history/${runId}`
                }
            },
            syncLog: (id: string, params?: Record<string, unknown>) =>
                `/assistants/${id}/details/sync-log${formatQueryParams(params)}`
        },
        threads: {
            detail: (
                assistantId: string,
                threadId: string,
                params?: Record<string, unknown>
            ) =>
                `/assistants/${assistantId}/threads/${threadId}${formatQueryParams(
                    params
                )}`
        }
    },

    // Share routes
    share: {
        thread: (token: string) => `/share/thread/${token}`
    },

    // Auth routes
    login: () => '/login',

    // Fallback route - redirects to home
    notFound: () => '*'
};

export type NavigationRoutes = typeof NavigationRoutes;
